import {
  getExportForwardedSifarishUser,
  getExportReportedSifarishUser,
  getExportSavedSifarishUser,
  getUserSifarishDetails,
  acceptBusinessProfileRequest,
  rejectBusinessProfileRequest,
} from "_services";
import { ButtonHeading } from "components";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Images } from "constants";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { IoArrowBack } from "react-icons/io5";
import { utils } from "helper";
import { AddSifarishModal } from "modals";

function hasAddressContent(addr) {
  if (!addr || typeof addr !== "object") return false;
  return [
    addr.addressLine1,
    addr.addressLine2,
    addr.city,
    addr.state,
    addr.country,
    addr.zipcode,
    addr.latitude,
    addr.longitude,
  ].some((v) => v !== undefined && v !== null && String(v).trim() !== "");
}

function toFixedCoordinate(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return num.toFixed(4);
}

function renderTagList(items, emptyText = "—") {
  if (!Array.isArray(items) || items.length === 0) {
    return <span style={{ color: "#666" }}>{emptyText}</span>;
  }
  const cleaned = items.map((x) => String(x || "").trim()).filter(Boolean);
  if (cleaned.length === 0) {
    return <span style={{ color: "#666" }}>{emptyText}</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
      {cleaned.map((text, idx) => (
        <span
          key={`${text}-${idx}`}
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: 6,
            background: "#eef2f7",
            fontSize: 13,
          }}
        >
          {text}
        </span>
      ))}
    </div>
  );
}

function normalizeTrustList(value) {
  if (Array.isArray(value)) {
    return value.map((x) => String(x || "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return [];
}

function getTrustList(data, keys = []) {
  for (const key of keys) {
    const normalized = normalizeTrustList(data?.[key]);
    if (normalized.length > 0) return normalized;
  }
  return [];
}

function getDirectionsUrl(addr, title) {
  if (!addr || typeof addr !== "object") return "";
  const lat = Number(addr.latitude);
  const lng = Number(addr.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  const fallback = [title, addr.addressLine1, addr.addressLine2, addr.city, addr.state]
    .filter((x) => x && String(x).trim())
    .join(", ");
  if (!fallback) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    fallback
  )}`;
}

function formatAddressLines(addr) {
  if (!hasAddressContent(addr)) return [];
  const line1 = [addr.addressLine1, addr.addressLine2]
    .filter((x) => x && String(x).trim())
    .join(", ");
  const cityStateZip = [addr.city, addr.state, addr.zipcode]
    .filter((x) => x && String(x).trim())
    .join(", ");
  const lines = [];
  if (line1) lines.push(line1);
  if (cityStateZip) lines.push(cityStateZip);
  if (addr.country && String(addr.country).trim())
    lines.push(String(addr.country).trim());
  const lat = toFixedCoordinate(addr.latitude);
  const lng = toFixedCoordinate(addr.longitude);
  if (lat || lng) {
    lines.push(
      [lat && `Lat ${lat}`, lng && `Lng ${lng}`].filter(Boolean).join(" · ")
    );
  }
  return lines;
}

function isImageFileUrl(url) {
  if (!url) return false;
  const clean = String(url).split("?")[0].toLowerCase();
  return /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/.test(clean);
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

const SifarishDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { listData, currentIndexx } = location.state || {};
  const [activeIndex, setActiveIndex] = useState(currentIndexx ?? 0);
  const [sifarishDetails, setSifarishDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const [userData, setUserData] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxItems, setLightboxItems] = useState([]);
  const lightboxSliderRef = useRef(null);
  const mediaSliderRef = useRef(null);
  const [mediaCurrentIndex, setMediaCurrentIndex] = useState(0);

  const [isOpen, setIsOpen] = useState(false);
  const [sifarishToEdit, setSifarishToEdit] = useState(null);
  const [allSifarishToEdit, setAllSifarishToEdit] = useState([]);
  const [sifarishInd, setSifarishInd] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const actionBtnStyle = {
    background: "linear-gradient(180deg, #123b69 0%, #0d2f55 100%)",
    border: "1px solid rgba(255,255,255,0.12)",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.2,
    boxShadow: "0 6px 16px rgba(13, 47, 85, 0.25)",
    transition: "all 0.2s ease",
    letterSpacing: "0.1px",
  };
  const navBtnStyle = {
    ...actionBtnStyle,
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 12.5,
  };
  const panelStyle = {
    background: "#ffffff",
    border: "1px solid #e8edf5",
    borderRadius: 14,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
  };

  const subcategoryTitle =
    sifarishDetails?.categoryId?.subCategories?.length > 0
      ? sifarishDetails.categoryId.subCategories
          .filter((sub) => sub._id === sifarishDetails.subCategoryId)
          .map((sub) => sub.title)
          .join(", ")
      : "";
  const isEventSubcategory = String(subcategoryTitle || "")
    .toLowerCase()
    .includes("event");
  const eventStartText = formatDateTime(sifarishDetails?.eventStartDateTime);
  const eventEndText = formatDateTime(sifarishDetails?.eventEndDateTime);

  const openLightbox = (items, startIndex = 0) => {
    const cleaned = Array.isArray(items)
      ? items
          .map((item) => ({
            url: item?.url || "",
            name: item?.name || "",
          }))
          .filter((item) => item.url)
      : [];
    if (cleaned.length === 0) return;
    const idx = Math.max(0, Math.min(startIndex, cleaned.length - 1));
    setLightboxItems(cleaned);
    setCurrentIndex(idx);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxItems([]);
    setCurrentIndex(0);
  };

  const downloadLightboxItem = () => {
    const item = lightboxItems?.[currentIndex];
    const fileUrl = item?.url;
    if (!fileUrl) return;
    const fallbackName = `sifarish-media-${currentIndex + 1}`;
    const guessedName = String(fileUrl).split("?")[0].split("/").pop() || "";
    const filename = item?.name || guessedName || fallbackName;
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("userData");
    if (storedUser) {
      setUserData(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    setMediaCurrentIndex(0);
  }, [sifarishDetails?._id]);

  const userId = userData?._id;
  const sifarishId = searchParams.get("sifarishId");

  useEffect(() => {
    if (!userId) return;
    if (listData && listData.length > 0) return;

    if (sifarishId) {
      fetchSifarishDetails(sifarishId);
    }
  }, [userId, sifarishId]);

  useEffect(() => {
    if (!userId) return;
    if (!listData || !listData[activeIndex]) return;

    const newSifarishId = listData[activeIndex]._id;

    navigate(
      `/sifarish/getSifarishes?userId=${userId}&sifarishId=${newSifarishId}`,
      {
        replace: true,
        state: { listData, currentIndexx: activeIndex },
      }
    );

    fetchSifarishDetails(newSifarishId);
  }, [activeIndex, userId]);

  const fetchSifarishDetails = async (sifarishIdParam) => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const signal = controller.signal;

    try {
      // const payload = { userId, sifarishId };
      const payload = {
        userId,
        sifarishId: sifarishIdParam || sifarishId,
      };
      const res = await getUserSifarishDetails(payload, { signal });

      if (res?.data?.sifarish) {
        setSifarishDetails(res.data.sifarish);
      } else {
        throw new Error(res?.responseMessage || "No data found");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        setError(error.message || "Something went wrong");
        toast.error(error.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    draggable: true,
  };

  const exportSavedSifarishToCSV = async () => {
    try {
      const payload = {
        userId: userId,
        sifarishId: sifarishId,
        page: 1,
        limit: -1,
      };

      const res = await getExportSavedSifarishUser(payload);

      if (!res || !res.data?.users || res.data.users.length === 0) {
        toast.error("No data to export!");
        return;
      }

      const fullData = res.data.users;

      const csvHeaders = [
        "Serial No",
        "User",
        "Bird Name",
        "Phone No.",
        "Sifarish Score",
        "Recommended Status",
        "Generation",
        "Location",
      ];

      const csvRows = fullData.map((user, index) => [
        index + 1,
        `"${user?.fromUser?.firstName || "-"} ${user?.fromUser?.lastName || "-"
        }"`,
        user?.fromUser?.birdId?.title || "-",
        `"${user?.fromUser?.countryCode || "-"} ${user?.fromUser?.phoneNumber || "-"
        }"`,
        user?.fromUser?.sifarishPoints || "-",
        user?.isRecommended === true ? "Recommended" : "Not Recommended",
        user?.fromUser?.generationId?.title || "-",
        `"${user?.fromUser?.city || "-"}, ${user?.fromUser?.state || "-"}, ${user?.fromUser?.country || "-"
        }"`,
      ]);

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [csvHeaders.join(","), ...csvRows.map((row) => row.join(","))].join(
          "\n"
        );

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.href = encodedUri;
      const epochTime = Date.now();
      link.download = `Saved_Sifarish_User_${epochTime}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error(error?.message || "Something went wrong during export.");
    }
  };

  const exportForwardSifarishToCSV = async () => {
    try {
      const payload = {
        userId: userId,
        sifarishId: sifarishId,
        page: 1,
        limit: -1,
      };

      const res = await getExportForwardedSifarishUser(payload);

      if (!res || !res.data?.users || res.data.users.length === 0) {
        toast.error("No data to export!");
        return;
      }

      const fullData = res.data.users;

      const csvHeaders = [
        "Serial No",
        "User",
        "Bird Name",
        "Phone No.",
        "Sifarish Score",
        "Generation",
        "Location",
      ];

      const csvRows = fullData.map((user, index) => [
        index + 1,
        `"${user?.fromUserId?.firstName || "-"} ${user?.fromUserId?.lastName || "-"
        }"`,
        user?.fromUserId?.birdId?.title || "-",
        `"${user?.fromUserId?.countryCode || "-"} ${user?.fromUserId?.phoneNumber || "-"
        }"`,
        user?.fromUserId?.sifarishPoints || "-",
        user?.fromUserId?.generationId?.title || "-",
        `"${user?.fromUserId?.city || "-"}, ${user?.fromUserId?.state || "-"
        }, ${user?.fromUserId?.country || "-"}"`,
      ]);

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [csvHeaders.join(","), ...csvRows.map((row) => row.join(","))].join(
          "\n"
        );

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.href = encodedUri;
      const epochTime = Date.now();
      link.download = `Forwarded_Sifarish_User_${epochTime}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error(error?.message || "Something went wrong during export.");
    }
  };

  const exportReportedSifarishToCSV = async () => {
    try {
      const payload = {
        userId: userId,
        sifarishId: sifarishId,
        page: 1,
        limit: -1,
      };

      const res = await getExportReportedSifarishUser(payload);

      if (!res || !res.data?.users || res.data.users.length === 0) {
        toast.error("No data to export!");
        return;
      }

      const fullData = res.data.users;

      const csvHeaders = [
        "Serial No",
        "User",
        "Bird Name",
        "Phone No.",
        "Sifarish Score",
        "Generation",
        "Location",
        "Reported Reason",
      ];

      const csvRows = fullData.map((user, index) => [
        index + 1,
        `"${user?.fromUserId?.firstName || "-"} ${user?.fromUserId?.lastName || "-"
        }"`,
        user?.fromUserId?.birdId?.title || "-",
        `"${user?.fromUserId?.countryCode || "-"} ${user?.fromUserId?.phoneNumber || "-"
        }"`,
        user?.fromUserId?.sifarishPoints || "-",
        user?.fromUserId?.generationId?.title || "-",
        `"${user?.fromUserId?.city || "-"}, ${user?.fromUserId?.state || "-"
        }, ${user?.fromUserId?.country || "-"}"`,
        `"${user?.reasonId?.title || "-"}"`,
      ]);

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [csvHeaders.join(","), ...csvRows.map((row) => row.join(","))].join(
          "\n"
        );

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.href = encodedUri;
      const epochTime = Date.now();
      link.download = `Reported_Sifarish_User_${epochTime}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error(error?.message || "Something went wrong during export.");
    }
  };

  const handleBackButton = () => {
    navigate(-1);
  };

  const isBusinessProfileRequest =
    sifarishDetails?.isActive === false &&
    sifarishDetails?.isDeleted === false &&
    sifarishDetails?.isRegisteredByBusiness === true;

  const handleApprove = async () => {
    if (
      !window.confirm(
        "Are you sure you want to approve this business request? It will be marked as active."
      )
    )
      return;
    if (!userId || !sifarishId) return;
    setActionLoading(true);
    try {
      const res = await acceptBusinessProfileRequest({
        userId,
        sifarishId,
      });
      if (res?.status === 1) {
        toast.success(res?.message || "Approved.");
        fetchSifarishDetails(sifarishId);
      } else {
        toast.error(res?.message || "Failed to approve.");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (
      !window.confirm(
        "Are you sure you want to reject this business request? It will be marked as deleted."
      )
    )
      return;
    if (!userId || !sifarishId) return;
    setActionLoading(true);
    try {
      const res = await rejectBusinessProfileRequest({
        userId,
        sifarishId,
      });
      if (res?.status === 1) {
        toast.success(res?.message || "Rejected.");
        navigate("/business_profile_requests");
      } else {
        toast.error(res?.message || "Failed to reject.");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <ButtonHeading
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span
              role="button"
              tabIndex={0}
              onClick={handleBackButton}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleBackButton();
              }}
              style={{ display: "inline-flex", cursor: "pointer" }}
              aria-label="Back"
            >
              <IoArrowBack />
            </span>
            <span>{sifarishDetails?.title || "Sifarish Details"}</span>
          </span>
        }
        extraRightContent={
          <>
            {isBusinessProfileRequest ? (
            <>
              <div className="_rSideBtn">
                <button
                  className="_addComm_btn"
                  style={{ background: "#198754" }}
                  onClick={handleApprove}
                  disabled={actionLoading}
                >
                  {actionLoading ? "..." : "Approve"}
                </button>
              </div>
              <div className="_rSideBtn">
                <button
                  className="_addComm_btn"
                  style={{ background: "var(--reject-red)" }}
                  onClick={handleReject}
                  disabled={actionLoading}
                >
                  Reject
                </button>
              </div>
            </>
            ) : null}
            <div className="_rSideBtn">
              <button
                className="_addComm_btn"
                onClick={() => {
                  setSifarishToEdit(sifarishDetails);
                  setAllSifarishToEdit(listData || []);
                  setSifarishInd(activeIndex);
                  setIsOpen(true);
                }}
              >
                Edit
              </button>
            </div>
          </>
        }
      />

      <div className="d-flex justify-content-between px-3 mt-3">
        <button
          disabled={activeIndex === 0}
          className="__birdTag"
          style={navBtnStyle}
          onClick={() => setActiveIndex((prev) => prev - 1)}
        >
          ⬅ Previous
        </button>

        <button
          disabled={activeIndex === listData.length - 1}
          className="__birdTag"
          style={navBtnStyle}
          onClick={() => setActiveIndex((prev) => prev + 1)}
        >
          Next ➜
        </button>
      </div>

      <div>
        {loading ? (
          <p>Loading Sifarish Details...</p>
        ) : error ? (
          <p className="text-red-500">Error: {error}</p>
        ) : sifarishDetails ? (
          <>
            <div
              className="py-4"
              style={{ maxWidth: 1200, margin: "0 auto", paddingInline: 16 }}
            >
              <div
                className=" __sifarisD_head  mb-3 "
                style={{
                  ...panelStyle,
                  flexDirection: "column",
                  alignItems: "stretch",
                  padding: 16,
                }}
              >
                <div className="__sifDetail_bxs" style={{ display: "flex", alignItems: "flex-start", gap: "12px", width: "100%" }}>
                  {sifarishDetails?.logoUrl && (
                    <img
                      src={sifarishDetails.logoUrl}
                      alt="Logo"
                      width={50}
                      height={50}
                      style={{
                        borderRadius: 10,
                        objectFit: "cover",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                      onClick={() => {
                        openLightbox([
                          {
                            url: sifarishDetails.logoUrl,
                            name: `${sifarishDetails?.title || "logo"}-logo.jpg`,
                          },
                        ]);
                      }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ marginBottom: 0 }}>
                      Description: {sifarishDetails?.description || ""}
                    </p>
                    <p style={{ margin: "6px 0 0" }}>
                      <b>Category: </b>
                      {sifarishDetails?.categoryId?.title || "-"}
                      {subcategoryTitle ? ` | ${subcategoryTitle}` : ""}
                    </p>
                    {isEventSubcategory && (eventStartText || eventEndText) && (
                      <p style={{ margin: "6px 0 0" }}>
                        <b>Event: </b>
                        {eventStartText ? `Start ${eventStartText}` : "Start -"}
                        {" | "}
                        {eventEndText ? `End ${eventEndText}` : "End -"}
                      </p>
                    )}
                  </div>
                </div>

                <div className="__rSide_btn" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                  <button
                    className="__birdTag "
                    style={actionBtnStyle}
                    onClick={() => exportSavedSifarishToCSV()}
                  >
                    Export saved sifarish users
                  </button>
                  <button
                    className="__birdTag "
                    style={actionBtnStyle}
                    onClick={() => exportForwardSifarishToCSV()}
                  >
                    Export forwarded sifarish users
                  </button>
                  <button
                    className="__birdTag "
                    style={actionBtnStyle}
                    onClick={() => exportReportedSifarishToCSV()}
                  >
                    Export reported sifarish users
                  </button>

                </div>
              </div>

              {/* Media URLs - only media */}
              <div
                className="mb-4"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
                  gap: 16,
                  alignItems: "start",
                }}
              >
                <div
                  style={{
                    ...panelStyle,
                    padding: 12,
                  }}
                >
                  <h6 className="mb-2">Media</h6>
                  {sifarishDetails?.mediaUrls?.length > 0 ? (
                    sifarishDetails.mediaUrls.length === 1 ? (
                      <img
                        src={
                          sifarishDetails.mediaUrls[0]?.url ||
                          "https://via.placeholder.com/400x300"
                        }
                        alt=""
                        width={360}
                        height={420}
                        className="_slidImgOne"
                        style={{
                          cursor: "pointer",
                        width: "100%",
                          height: 420,
                          objectFit: "contain",
                          marginTop: 8,
                        }}
                        onClick={() => {
                          openLightbox(sifarishDetails?.mediaUrls || [], 0);
                        }}
                      />
                    ) : (
                      <div style={{ width: "100%", marginTop: 8, position: "relative" }}>
                        <Slider
                          {...sliderSettings}
                          className="__anvSlickSlider"
                          ref={mediaSliderRef}
                          infinite={false}
                          beforeChange={(_, next) => setMediaCurrentIndex(next)}
                        >
                          {sifarishDetails.mediaUrls.map((item, index) => (
                            <div key={index}>
                              <img
                                onClick={() => {
                                  openLightbox(sifarishDetails?.mediaUrls || [], index);
                                }}
                                src={
                                  item?.url || "https://via.placeholder.com/400x300"
                                }
                                alt=""
                                width={360}
                                height={420}
                                className="_slidImg"
                                style={{ width: "100%", height: 420, objectFit: "contain" }}
                              />
                            </div>
                          ))}
                        </Slider>
                        <button
                          className="media-slider-arrow media-slider-arrow--left"
                          style={{
                            left: 10,
                            right: "auto",
                            top: "50%",
                            transform: "translateY(-50%)",
                            opacity: mediaCurrentIndex === 0 ? 0.35 : 1,
                            pointerEvents: mediaCurrentIndex === 0 ? "none" : "auto",
                            width: 30,
                            height: 30,
                            fontSize: 22,
                            lineHeight: 1,
                          }}
                          onClick={() => mediaSliderRef.current?.slickPrev?.()}
                        >
                          ‹
                        </button>
                        <button
                          className="media-slider-arrow media-slider-arrow--right"
                          style={{
                            right: 10,
                            top: "50%",
                            transform: "translateY(-50%)",
                            opacity:
                              mediaCurrentIndex ===
                              (sifarishDetails?.mediaUrls?.length || 1) - 1
                                ? 0.35
                                : 1,
                            pointerEvents:
                              mediaCurrentIndex ===
                              (sifarishDetails?.mediaUrls?.length || 1) - 1
                                ? "none"
                                : "auto",
                            width: 30,
                            height: 30,
                            fontSize: 22,
                            lineHeight: 1,
                          }}
                          onClick={() => mediaSliderRef.current?.slickNext?.()}
                        >
                          ›
                        </button>
                      </div>
                    )
                  ) : (
                    <p className="text-center mb-0">No Media Available</p>
                  )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ ...panelStyle, padding: 14 }}>
                    <h6 className="mb-2" style={{ fontSize: 18, fontWeight: 700 }}>
                      Quick info
                    </h6>
                    <p style={{ margin: "0 0 6px", fontSize: 15 }}>
                      <b>Total Forwarded:</b> {sifarishDetails?.totalForwarded || 0}
                    </p>
                    <p style={{ margin: "0 0 6px", fontSize: 15 }}>
                      <b>Total Recommended:</b>{" "}
                      {sifarishDetails?.totalRecommendedReviews || 0}
                    </p>
                    <p style={{ margin: "0 0 6px", fontSize: 15 }}>
                      <b>QR Scanned:</b> {sifarishDetails?.qrScanCount || 0}
                    </p>
                    <p style={{ margin: 0, fontSize: 15 }}>
                      <b>Total Reviews:</b> {sifarishDetails?.totalReviews || 0}
                    </p>
                  </div>

                  <div style={{ ...panelStyle, padding: 14 }}>
                    <h6 className="mb-2" style={{ fontSize: 18, fontWeight: 700 }}>
                      Trust profile
                    </h6>
                    <div className="mb-2">
                      <p className="mb-1" style={{ fontSize: 15, fontWeight: 700 }}>
                        What do people come back for?
                      </p>
                      {renderTagList(
                        getTrustList(sifarishDetails, [
                          "trustComebackReasons",
                          "trust_comeback_reasons",
                          "comebackReasons",
                        ]).length > 0
                          ? getTrustList(sifarishDetails, [
                              "trustComebackReasons",
                              "trust_comeback_reasons",
                              "comebackReasons",
                            ])
                          : getTrustList(sifarishDetails, ["offering"]),
                        "No data available"
                      )}
                    </div>
                    <div className="mb-2">
                      <p className="mb-1" style={{ fontSize: 15, fontWeight: 700 }}>
                        Best for
                      </p>
                      {renderTagList(
                        getTrustList(sifarishDetails, [
                          "trustBestFor",
                          "trust_best_for",
                          "bestFor",
                        ]),
                        "No data available"
                      )}
                    </div>
                    <div className="mb-0">
                      <p className="mb-1" style={{ fontSize: 15, fontWeight: 700 }}>
                        Why do people recommend you?
                      </p>
                      {renderTagList(
                        getTrustList(sifarishDetails, [
                          "trustRecommendReasons",
                          "trust_recommend_reasons",
                          "recommendReasons",
                        ]),
                        "No data available"
                      )}
                    </div>
                  </div>

                  {sifarishDetails?.businessProofUrl && (
                    <div style={{ ...panelStyle, padding: 14 }}>
                      <h6 className="mb-2" style={{ fontSize: 18, fontWeight: 700 }}>
                        Business proof
                      </h6>
                      {isImageFileUrl(sifarishDetails.businessProofUrl) ? (
                        <img
                          src={sifarishDetails.businessProofUrl}
                          alt="Business proof"
                          width={220}
                          height={140}
                          style={{
                            width: "100%",
                            maxWidth: 260,
                            height: "auto",
                            cursor: "pointer",
                            borderRadius: 8,
                            border: "1px solid #e5e7eb",
                          }}
                          onClick={() => {
                            openLightbox([
                              {
                                url: sifarishDetails.businessProofUrl,
                                name: `${sifarishDetails?.title || "business-proof"}-proof`,
                              },
                            ]);
                          }}
                        />
                      ) : (
                        <a
                          href={sifarishDetails.businessProofUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 10,
                            textDecoration: "none",
                            color: "#111827",
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: "10px 12px",
                            background: "#fff",
                          }}
                        >
                          <img
                            src={Images.PDFICON}
                            alt="PDF"
                            width={24}
                            height={24}
                            style={{ objectFit: "contain" }}
                          />
                          <span style={{ fontWeight: 600 }}>Open Business proof</span>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              className="px-4 pb-4"
              style={{ maxWidth: 1200, margin: "0 auto", paddingInline: 16 }}
            >
              <div
                className="__userView "
                style={{
                  ...panelStyle,
                  alignItems: "stretch",
                  padding: 16,
                  gap: 16,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                }}
              >
                <div style={{ ...panelStyle, padding: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    <img
                      src={
                        sifarishDetails?.createdByUserId?.userProfileImageUrl ||
                        Images.USERLIMG
                      }
                      alt="user Img"
                      style={{ borderRadius: "50%" }}
                      width={54}
                      height={54}
                    />
                    <div>
                      <h4 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
                        {sifarishDetails?.createdByUserId?.firstName}{" "}
                        {sifarishDetails?.createdByUserId?.lastName}
                      </h4>
                      <p style={{ margin: "2px 0 0", fontSize: 14, color: "#64748b" }}>
                        {sifarishDetails?.createdByUserId?.phoneNumber || "-"}
                      </p>
                    </div>
                  </div>

                  <p style={{ margin: "0 0 8px", fontSize: 14 }}>
                    <b style={{ color: "#111827" }}>Created Date:</b>{" "}
                    <span style={{ color: "#475569" }}>
                      {sifarishDetails?.createdAt
                        ? utils?.formatDateToDDMMYYYY(sifarishDetails?.createdAt)
                        : "-"}
                    </span>
                  </p>
                  <p style={{ margin: "0 0 8px", fontSize: 14 }}>
                    <b style={{ color: "#111827" }}>Contact Name:</b>{" "}
                    <span style={{ color: "#475569" }}>
                      {sifarishDetails?.contactName || "-"}
                      {sifarishDetails?.contactPersonRole
                        ? ` | ${sifarishDetails.contactPersonRole}`
                        : ""}
                    </span>
                  </p>
                  <p style={{ margin: "0 0 8px", fontSize: 14 }}>
                    <b style={{ color: "#111827" }}>Contact Email:</b>{" "}
                    <span style={{ color: "#475569" }}>
                      {sifarishDetails?.email || "-"}
                    </span>
                  </p>
                  <p style={{ margin: "0 0 12px", fontSize: 14 }}>
                    <b style={{ color: "#111827" }}>Contact Number:</b>{" "}
                    <span style={{ color: "#475569" }}>
                      {sifarishDetails?.phoneNumber || "-"}
                    </span>
                  </p>

                  <div style={{ ...panelStyle, padding: 12 }}>
                    <h6 style={{ margin: "0 0 10px", fontSize: 16, fontWeight: 700 }}>
                      Links
                    </h6>
                    {sifarishDetails?.links?.length > 0 ? (
                      <div
                        style={{ display: "flex", flexDirection: "column", gap: 8 }}
                      >
                        {sifarishDetails.links.map((item, index) => {
                          const linkType =
                            item?.linkType?.title || item?.linkType?.name || "Link";
                          return (
                            <div
                              key={`${item?.url || "link"}-${index}`}
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                                alignItems: "center",
                                fontSize: 14,
                              }}
                            >
                              <span style={{ fontWeight: 700, color: "#111827" }}>
                                {linkType}
                              </span>
                              <span style={{ color: "#94a3b8" }}>|</span>
                              <Link
                                to={item?.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {item?.url}
                              </Link>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mb-0" style={{ color: "#64748b", fontSize: 14 }}>
                        No Links Available
                      </p>
                    )}
                  </div>
                </div>

                <div style={{ ...panelStyle, padding: 16 }}>
                  <h6 style={{ margin: "0 0 10px", fontSize: 16, fontWeight: 700 }}>
                    Address
                  </h6>
                  {hasAddressContent(sifarishDetails?.address) ? (
                    <>
                      <div
                        style={{
                          whiteSpace: "pre-line",
                          lineHeight: 1.6,
                          color: "#475569",
                          fontSize: 14,
                        }}
                      >
                        {formatAddressLines(sifarishDetails.address).join("\n")}
                      </div>
                      {getDirectionsUrl(
                        sifarishDetails?.address,
                        sifarishDetails?.title
                      ) && (
                        <div className="mt-2">
                          <a
                            href={getDirectionsUrl(
                              sifarishDetails?.address,
                              sifarishDetails?.title
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Get Direction
                          </a>
                        </div>
                      )}
                    </>
                  ) : (
                    <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
                      Address not available
                    </p>
                  )}
                </div>

              </div>
            </div>



            {lightboxOpen && (
              <div
                className="lightbox-overlay"
                onClick={closeLightbox}
                onKeyDown={(e) => {
                  if (e.key === "Escape") closeLightbox();
                  if (e.key === "ArrowLeft") {
                    lightboxSliderRef.current?.slickPrev?.();
                  }
                  if (e.key === "ArrowRight") {
                    lightboxSliderRef.current?.slickNext?.();
                  }
                }}
                tabIndex={0}
              >
                <div
                  className="lightbox-container"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="lightbox-close"
                    onClick={closeLightbox}
                  >
                    ✕
                  </button>
                  <div
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 56,
                      zIndex: 2,
                      display: "flex",
                      gap: 8,
                    }}
                  >
                    <button
                      className="lightbox-download-btn"
                      style={{
                        position: "static",
                        width: "auto",
                        height: "auto",
                        borderRadius: 8,
                        padding: "6px 10px",
                        fontSize: 12,
                      }}
                      onClick={downloadLightboxItem}
                    >
                      Download
                    </button>
                  </div>
                  {lightboxItems.length > 1 && (
                    <>
                      <button
                        className="media-slider-arrow lightbox-nav-arrow"
                        style={{
                          left: 12,
                          right: "auto",
                          top: "50%",
                          transform: "translateY(-50%)",
                          opacity: currentIndex === 0 ? 0.4 : 1,
                          pointerEvents: currentIndex === 0 ? "none" : "auto",
                        }}
                        onClick={() => {
                          lightboxSliderRef.current?.slickPrev?.();
                        }}
                      >
                        ‹
                      </button>
                      <button
                        className="media-slider-arrow lightbox-nav-arrow"
                        style={{
                          right: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          opacity: currentIndex === lightboxItems.length - 1 ? 0.4 : 1,
                          pointerEvents:
                            currentIndex === lightboxItems.length - 1
                              ? "none"
                              : "auto",
                        }}
                        onClick={() => {
                          lightboxSliderRef.current?.slickNext?.();
                        }}
                      >
                        ›
                      </button>
                    </>
                  )}

                  <Slider
                    {...sliderSettings}
                    infinite={false}
                    initialSlide={currentIndex}
                    ref={lightboxSliderRef}
                    beforeChange={(_, next) => setCurrentIndex(next)}
                  >
                    {lightboxItems.map((item, index) => (
                      <div key={index} className="">
                        <img
                          src={
                            item?.url || "https://via.placeholder.com/800x600"
                          }
                          alt=""
                          className="lightbox-image"
                        />
                      </div>
                    ))}
                  </Slider>
                </div>
              </div>
            )}
          </>
        ) : (
          <p>No Sifarish details available.</p>
        )}
      </div>

      {isOpen && (
        <AddSifarishModal
          isOpen={isOpen}
          closeModal={() => setIsOpen(false)}
          modalMainClass="_dashCommon_Modal _lgModal"
          modalTitle={sifarishToEdit ? "Edit Sifarish" : "Add Sifarish"}
          footerPart={true}
          submitTitle={sifarishToEdit ? "Update Sifarish" : "Add Sifarish"}
          setFilter={{}}
          filter={{}}
          userData={userData}
          onSuccess={() => fetchSifarishDetails(sifarishId)}
          sifarishToEdit={sifarishToEdit}
          sifarishArrayToEdit={{ allSifarishToEdit }}
          sifarishInd={sifarishInd}
        />
      )}
    </>
  );
};

export default SifarishDetail;
